/**
 * SettingsTaxes — Iter 191t · Selling › Taxes & Duties
 *
 * Shopify/Squarespace parity for tax configuration:
 *   1. Two collection modes — Manual rates by region, or Auto-calc via a
 *      third-party provider (Avalara, TaxJar).
 *   2. Location-based tax rules — Add tax rules per region (state/province/country)
 *      because taxes move differently by jurisdiction.
 *   3. Third-party tax apps — install/connect Avalara/TaxJar so we can auto-
 *      calculate at checkout once wired up server-side.
 *
 * Persistence:
 *   GET  /api/builder/settings          → returns `taxes`
 *   PUT  /api/builder/settings { taxes: {...} }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Save, Loader2, Plus, Trash2, MapPin, Zap, Wrench, CheckCircle2, Info,
  ExternalLink, Plug,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULTS = {
  mode: 'manual',              // 'manual' | 'auto'
  prices_include_tax: false,
  charge_tax_on_shipping: true,
  origin_country: 'US',
  origin_region: '',
  origin_postal: '',
  rules: [],                    // manual: [{ id, name, country, region, postal, rate, kind }]
  provider: '',                 // auto: 'avalara' | 'taxjar' | ''
  provider_api_key: '',
  provider_connected: false,
  third_party_apps: [],
};

const RULE_KINDS = [
  { id: 'sales_tax', label: 'Sales Tax (US)' },
  { id: 'gst', label: 'GST / HST' },
  { id: 'pst', label: 'PST / QST' },
  { id: 'vat', label: 'VAT' },
  { id: 'other', label: 'Other' },
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'other', name: 'Other' },
];

const APP_CATALOG = [
  { id: 'avalara', name: 'Avalara AvaTax', tagline: 'Real-time rooftop-accurate rates for 190+ countries', href: 'https://www.avalara.com', color: '#1e5aff' },
  { id: 'taxjar', name: 'TaxJar', tagline: 'AutoFile sales-tax returns for the US, plus real-time rates', href: 'https://www.taxjar.com', color: '#00c7b7' },
  { id: 'quaderno', name: 'Quaderno', tagline: 'EU VAT / GST compliance & invoicing', href: 'https://quaderno.io', color: '#ff5959' },
  { id: 'sovos', name: 'Sovos', tagline: 'Enterprise-grade global tax determination', href: 'https://sovos.com', color: '#7c3aed' },
];

export default function SettingsTaxes() {
  const [state, setState] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, { headers: auth() });
        setState({
          ...DEFAULTS,
          ...(data?.taxes || {}),
          rules: (data?.taxes?.rules || []),
          third_party_apps: (data?.taxes?.third_party_apps || []),
        });
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (key, val) => setState((p) => ({ ...p, [key]: val }));

  const addRule = () =>
    setState((p) => ({
      ...p,
      rules: [
        ...(p.rules || []),
        { id: `rule_${Date.now()}`, name: '', country: 'US', region: '', postal: '', rate: 0, kind: 'sales_tax' },
      ],
    }));

  const updateRule = (idx, key, val) =>
    setState((p) => {
      const next = [...(p.rules || [])];
      next[idx] = { ...next[idx], [key]: val };
      return { ...p, rules: next };
    });

  const removeRule = (idx) =>
    setState((p) => ({ ...p, rules: (p.rules || []).filter((_, i) => i !== idx) }));

  const toggleApp = (appId) =>
    setState((p) => {
      const installed = (p.third_party_apps || []).includes(appId);
      return {
        ...p,
        third_party_apps: installed
          ? p.third_party_apps.filter((a) => a !== appId)
          : [...(p.third_party_apps || []), appId],
      };
    });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { taxes: state }, { headers: auth() });
      toast.success('Tax settings saved');
    } catch { toast.error('Could not save taxes'); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative" data-testid="settings-taxes-page">
      <DashboardBackdrop />
      <div className="max-w-4xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Selling</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Taxes &amp; Duties</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Choose how you charge sales tax at checkout. Add per-region rules for the places you sell, or
              connect a third-party tax service to auto-calculate on every order.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="taxes-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Step 1 — Pick a mode */}
            <GlassCard className="p-6" data-testid="taxes-mode-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={Receipt} ramp="violet" />
                <div>
                  <h2 className="text-white font-bold font-syne">1 · How do you want to charge tax?</h2>
                  <p className="text-xs text-white/55 mt-0.5">You can switch modes any time — nothing is lost.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  {
                    id: 'manual',
                    icon: Wrench,
                    title: 'Manual rates',
                    body: 'You control the rate for each region you sell in. Best if you sell in a small number of jurisdictions.',
                  },
                  {
                    id: 'auto',
                    icon: Zap,
                    title: 'Auto-calc via provider',
                    body: 'Connect Avalara, TaxJar, Quaderno, or Sovos. Rates update automatically on every order.',
                  },
                ].map((opt) => {
                  const active = state.mode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => set('mode', opt.id)}
                      className={`text-left rounded-2xl p-5 border transition-all ${
                        active
                          ? 'bg-gradient-to-br from-[#8B5CF6]/22 to-[#3B82F6]/10 border-[#A78BFA]/60 shadow-[0_0_28px_rgba(139,92,246,0.25)]'
                          : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`taxes-mode-${opt.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <opt.icon size={18} className={active ? 'text-white' : 'text-[#A78BFA]'} />
                        <div className="flex-1">
                          <div className="text-white font-semibold text-sm flex items-center gap-2">
                            {opt.title}
                            {active && <CheckCircle2 size={14} className="text-emerald-400" />}
                          </div>
                          <p className="text-[11.5px] text-white/60 mt-1 leading-relaxed">{opt.body}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </GlassCard>

            {/* Step 2a — Manual rate rules */}
            {state.mode === 'manual' && (
              <GlassCard className="p-6" data-testid="taxes-rules-card">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="flex items-start gap-4">
                    <GradientIconTile icon={MapPin} ramp="cyan" />
                    <div>
                      <h2 className="text-white font-bold font-syne">2 · Location-based rules</h2>
                      <p className="text-xs text-white/55 mt-0.5">
                        Taxes move differently by jurisdiction — add one rule per region you sell in.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addRule}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white transition-colors"
                    data-testid="taxes-add-rule-btn"
                  >
                    <Plus size={12} /> Add rule
                  </button>
                </div>

                {(state.rules || []).length === 0 && (
                  <div className="text-[12px] text-[#64748B] italic py-6 text-center border border-dashed border-white/10 rounded-xl">
                    No tax rules yet. Add one for each region where you&rsquo;re registered to collect tax.
                  </div>
                )}

                <div className="space-y-3">
                  {(state.rules || []).map((rule, idx) => (
                    <div
                      key={rule.id || idx}
                      className="grid grid-cols-[1fr_120px_120px_100px_120px_100px_auto] gap-2 items-end p-3 rounded-xl bg-white/[0.03] border border-white/10"
                      data-testid={`taxes-rule-row-${idx}`}
                    >
                      <div>
                        <Label className="text-[10px] text-white/50">Name</Label>
                        <Input value={rule.name || ''} onChange={(e) => updateRule(idx, 'name', e.target.value)} placeholder="California sales tax" className="mt-1 bg-white/5 border-white/10 text-white text-xs rounded-md" data-testid={`taxes-rule-name-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/50">Kind</Label>
                        <select value={rule.kind || 'sales_tax'} onChange={(e) => updateRule(idx, 'kind', e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1.5" data-testid={`taxes-rule-kind-${idx}`}>
                          {RULE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/50">Country</Label>
                        <select value={rule.country || 'US'} onChange={(e) => updateRule(idx, 'country', e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1.5" data-testid={`taxes-rule-country-${idx}`}>
                          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/50">State/Prov</Label>
                        <Input value={rule.region || ''} onChange={(e) => updateRule(idx, 'region', e.target.value)} placeholder="CA" className="mt-1 bg-white/5 border-white/10 text-white text-xs rounded-md" data-testid={`taxes-rule-region-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/50">Postal (opt.)</Label>
                        <Input value={rule.postal || ''} onChange={(e) => updateRule(idx, 'postal', e.target.value)} placeholder="90210" className="mt-1 bg-white/5 border-white/10 text-white text-xs rounded-md" data-testid={`taxes-rule-postal-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/50">Rate %</Label>
                        <Input type="number" step="0.001" value={rule.rate || 0} onChange={(e) => updateRule(idx, 'rate', parseFloat(e.target.value) || 0)} placeholder="7.25" className="mt-1 bg-white/5 border-white/10 text-white text-xs rounded-md" data-testid={`taxes-rule-rate-${idx}`} />
                      </div>
                      <button
                        onClick={() => removeRule(idx)}
                        className="w-8 h-8 grid place-items-center rounded-md text-white/50 hover:text-white hover:bg-white/10 self-end mb-1"
                        title="Remove rule"
                        data-testid={`taxes-rule-remove-${idx}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Step 2b — Auto-calc provider */}
            {state.mode === 'auto' && (
              <GlassCard className="p-6" data-testid="taxes-provider-card">
                <div className="flex items-start gap-4 mb-5">
                  <GradientIconTile icon={Zap} ramp="cyan" />
                  <div>
                    <h2 className="text-white font-bold font-syne">2 · Connect a tax provider</h2>
                    <p className="text-xs text-white/55 mt-0.5">
                      Pick a provider and paste your API key. We&rsquo;ll auto-calc rates on every checkout.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Provider</Label>
                    <select
                      value={state.provider || ''}
                      onChange={(e) => set('provider', e.target.value)}
                      className="w-full mt-1.5 bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm"
                      data-testid="taxes-provider-select"
                    >
                      <option value="">Select…</option>
                      <option value="avalara">Avalara AvaTax</option>
                      <option value="taxjar">TaxJar</option>
                      <option value="quaderno">Quaderno</option>
                      <option value="sovos">Sovos</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">API key</Label>
                    <Input
                      type="password"
                      value={state.provider_api_key || ''}
                      onChange={(e) => set('provider_api_key', e.target.value)}
                      placeholder="Paste your provider API key"
                      className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                      data-testid="taxes-provider-key"
                    />
                  </div>
                </div>

                <div className="mt-4 p-3 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20 flex items-start gap-2">
                  <Info size={13} className="text-[#A78BFA] mt-0.5 flex-shrink-0" />
                  <p className="text-[11.5px] text-slate-300 leading-relaxed">
                    Server-side auto-calc will kick in as soon as we ship the {state.provider || 'provider'} integration.
                    Until then, saved keys are stored securely and manual rules (Step 3) run as a fallback.
                  </p>
                </div>
              </GlassCard>
            )}

            {/* Step 3 — Nexus origin */}
            <GlassCard className="p-6" data-testid="taxes-origin-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={MapPin} ramp="violet" />
                <div>
                  <h2 className="text-white font-bold font-syne">3 · Your ship-from origin</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    The state/postal you ship from. Some jurisdictions charge origin-based tax.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Country</Label>
                  <select value={state.origin_country} onChange={(e) => set('origin_country', e.target.value)} className="w-full mt-1.5 bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm" data-testid="taxes-origin-country">
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">State / Province</Label>
                  <Input value={state.origin_region} onChange={(e) => set('origin_region', e.target.value)} placeholder="OR" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="taxes-origin-region" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Postal</Label>
                  <Input value={state.origin_postal} onChange={(e) => set('origin_postal', e.target.value)} placeholder="97205" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="taxes-origin-postal" />
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <label className="flex items-center gap-3 text-sm text-white/85 cursor-pointer">
                  <input type="checkbox" checked={state.prices_include_tax} onChange={(e) => set('prices_include_tax', e.target.checked)} className="accent-[#A78BFA]" data-testid="taxes-prices-include" />
                  My product prices already include tax
                </label>
                <label className="flex items-center gap-3 text-sm text-white/85 cursor-pointer">
                  <input type="checkbox" checked={state.charge_tax_on_shipping} onChange={(e) => set('charge_tax_on_shipping', e.target.checked)} className="accent-[#A78BFA]" data-testid="taxes-tax-shipping" />
                  Charge tax on shipping charges
                </label>
              </div>
            </GlassCard>

            {/* Step 4 — Third-party tax apps */}
            <GlassCard className="p-6" data-testid="taxes-apps-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={Plug} ramp="fuchsia" />
                <div>
                  <h2 className="text-white font-bold font-syne">4 · Third-party tax apps</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Bookmark or &ldquo;install&rdquo; apps you plan to use. Once wired up server-side, invoices and receipts
                    will reflect the provider&rsquo;s rates automatically.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {APP_CATALOG.map((app) => {
                  const installed = (state.third_party_apps || []).includes(app.id);
                  return (
                    <div
                      key={app.id}
                      className="rounded-2xl p-4 bg-white/[0.03] border border-white/10 flex items-start justify-between gap-3"
                      data-testid={`taxes-app-${app.id}`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${app.color}22`, border: `1px solid ${app.color}44` }}>
                          <Receipt size={15} style={{ color: app.color }} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-sm flex items-center gap-2">
                            {app.name}
                            <a href={app.href} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white">
                              <ExternalLink size={11} />
                            </a>
                          </div>
                          <p className="text-[11px] text-white/55 mt-0.5">{app.tagline}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleApp(app.id)}
                        className={`px-3 py-1.5 rounded-lg text-[10.5px] font-bold uppercase tracking-wider ${
                          installed
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-white/10 text-white border border-white/15 hover:bg-white/15'
                        }`}
                        data-testid={`taxes-app-toggle-${app.id}`}
                      >
                        {installed ? 'Installed' : 'Install'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          </>
        )}
      </div>
    </div>
  );
}
